import { useCallback, useMemo } from 'react';
import { RenderIf } from 'react-rainbow-components';
import { useOpenModal } from '@rainbow-modules/hooks';
import ButtonIcon from 'components/ButtonIcon';
import Phone from 'components/icons/phone';
import SmsIcon from 'components/icons/sms';
import FacebookSolid from 'components/icons/facebookSolid';
import InstagramSolid from 'components/icons/instagramSolid';
import TiktokSolid from 'components/icons/tiktokSolid';
import Email from 'components/icons/email';
import getInitials from 'data/services/string/getInitials';
import { EntityGet } from 'data/firestore/types';
import { Customer } from 'data/firestore/agent/customer/types';
import useSMSChat from 'hooks/useSMSChat';
import Tags from 'components/Tags';
import { useParams } from 'react-router-dom';
import useAgentData from 'hooks/useAgentData';
import { useNexhealthPatientLink } from 'customApps/nexhealth/hooks';
import {
    AvatarContainer,
    AvatarFrame,
    NameLabel,
    NexhealthMark,
    StyledAvatar,
    QuickActions,
    StyledLink,
    StyledBadgeOverlay,
} from './styled';
import { CUSTOMER_CALL_MODAL } from '../../constants';

interface UserAvatarProps {
    src?: string;
    uid: string;
    displayName?: string;
    phoneNumber?: string;
    email?: string;
    facebookProfileURL?: string;
    instagramProfileURL?: string;
    tiktokProfileURL?: string;
    profileData?: EntityGet<Customer> | null;
}

const UserAvatar = ({
    uid,
    displayName = '',
    src,
    phoneNumber,
    email,
    facebookProfileURL,
    instagramProfileURL,
    tiktokProfileURL,
    profileData,
}: UserAvatarProps) => {
    const { agentId } = useParams();
    const { isAppEnabled } = useAgentData();
    const { data: linkResult } = useNexhealthPatientLink(
        agentId || '',
        uid,
        isAppEnabled('nexhealth'),
    );
    const [link] = linkResult?.data || [];
    const isNexhealthLinked = Boolean(link && !link.stale);
    const initials = useMemo(() => {
        if (displayName === 'Unknown') return null;
        return getInitials(displayName);
    }, [displayName]);
    const { openChat } = useSMSChat();
    const [openCustomerCallModal] = useOpenModal(CUSTOMER_CALL_MODAL);

    const handleSMSClick = useCallback(
        () => openChat({ ...profileData }),
        [openChat, profileData],
    );

    return (
        <AvatarContainer>
            <AvatarFrame>
                <StyledAvatar
                    src={src}
                    assistiveText={displayName}
                    initials={initials}
                    title={displayName}
                    value={uid}
                />
                <RenderIf isTrue={isNexhealthLinked}>
                    <NexhealthMark title="Linked to NexHealth" />
                </RenderIf>
            </AvatarFrame>
            <NameLabel>
                {displayName}
            </NameLabel>
            <Tags
                value={profileData?.tags}
                maxBadges={2}
                agentId={agentId as string}
                entity="customer"
            />
            <QuickActions>
                <RenderIf isTrue={phoneNumber}>
                    <ButtonIcon
                        icon={<Phone />}
                        onClick={() => openCustomerCallModal({ customer: profileData })}
                    />
                    <ButtonIcon
                        icon={(
                            <StyledBadgeOverlay
                                overlap="circle"
                                isHidden={!profileData?.hasNewMessage}
                            >
                                <SmsIcon />
                            </StyledBadgeOverlay>
                        )}
                        onClick={handleSMSClick}
                    />
                </RenderIf>
                <RenderIf isTrue={email}>
                    <StyledLink as="a" href={`mailto:${email}`}>
                        <Email />
                    </StyledLink>
                </RenderIf>
                <RenderIf isTrue={facebookProfileURL}>
                    <StyledLink to={String(facebookProfileURL)} target="_blank">
                        <FacebookSolid />
                    </StyledLink>
                </RenderIf>
                <RenderIf isTrue={instagramProfileURL}>
                    <StyledLink to={String(instagramProfileURL)} target="_blank">
                        <InstagramSolid />
                    </StyledLink>
                </RenderIf>
                <RenderIf isTrue={tiktokProfileURL}>
                    <StyledLink to={String(tiktokProfileURL)} target="_blank">
                        <TiktokSolid />
                    </StyledLink>
                </RenderIf>
            </QuickActions>
        </AvatarContainer>
    );
};

export default UserAvatar;
